import React from "react";
import { Icon } from "../icons";
import { fmtDateTime } from "../lib/format";
import { HourlyFinal } from "./HourlyPricing";
import { PmIssues } from "./PmIssues";
import { AiCallBlock, CallbackWidget, PlannerCrew, ReadField, S2Field, SaveButton, SectionLabel, makeS2Setter, NoteTextarea, SalespersonField } from "./fields";

/**
 * "Post-move call completed": the MC marks the call made here (or presses Call client in the
 * footer). It unlocks "Post Move Completed", which sits in the footer next to the other buttons.
 */
export const PostMoveCallDone = ({ job, actions }) => {
  if (job.postMoveClosed) return null;
  return job.postMoveCallDone === true ? (
    <span className="badge resolved">
      <span className="bdot" />
      Post-move call completed
      {job.postMoveCallDoneAt ? " · " + fmtDateTime(job.postMoveCallDoneAt) : ""}
    </span>
  ) : (
    <button type="button" className="btn small" onClick={() => actions.completePostMoveCall(job.id)}>
      <Icon name="phone" />
      Post-move call completed
    </button>
  );
};

/**
 * "Post Move Completed", gated on the post-move call. In the footer, lined up with Call client
 * and the attempt button, per the prototype (Asana 1220099429071097, 7.3).
 */
export const PostMoveCompleteButton = ({ job, actions }) => {
  if (job.postMoveClosed) return null;
  const called = job.postMoveCallDone === true;
  return (
    <button
      type="button"
      className="btn success"
      disabled={!called}
      title={called ? undefined : "Call the client first"}
      onClick={() => actions.closePostMove(job.id)}
    >
      <Icon name="check" />
      Post Move Completed
    </button>
  );
};

const RATINGS = [
  ["great", "Great", "on-yes"],
  ["okay", "Okay", "on-mid"],
  ["poor", "Poor", "on-no"],
];
const RECOMMEND = [
  ["yes", "Yes", "on-yes"],
  ["maybe", "Maybe", "on-mid"],
  ["no", "No", "on-no"],
];
const LIKED = ["On time", "Careful with items", "Friendly crew", "Fast", "Clear communication", "Fair price"];
const IMPROVE = ["Arrival time", "Damage / missing items", "Communication", "Billing", "Crew conduct", "Packing"];

/** A Great / Okay / Poor (or Yes / Maybe / No) switch. */
const Seg3 = ({ label, options, value, onPick }) => (
  <div className="seg" role="group" aria-label={label}>
    {options.map(([v, text, on]) => (
      <button
        type="button"
        key={v}
        className={value === v ? on : ""}
        aria-pressed={value === v}
        onClick={() => onPick(value === v ? "" : v)}
      >
        {text}
      </button>
    ))}
  </div>
);

/**
 * LD "Feedback & Review", the prototype's card (Asana 1220099429071097, 7.2): ratings, what stood
 * out / what to do better, would they recommend us, the client's words and the internal notes.
 */
const PmFeedbackCard = ({ job, actions, set }) => {
  const fb = job.pmFeedback || {};
  const save = (patch) => actions.setJobField(job.id, "pmFeedback", { ...fb, ...patch });
  const toggle = (key, chip) => {
    const list = Array.isArray(fb[key]) ? fb[key] : [];
    save({ [key]: list.includes(chip) ? list.filter((c) => c !== chip) : list.concat(chip) });
  };
  const chips = (key, list, on) => (
    <div className="pm-chips" role="group" aria-label={key === "liked" ? "What stood out?" : "What could we do better?"}>
      {list.map((c) => {
        const picked = (fb[key] || []).includes(c);
        return (
          <button
            type="button"
            key={c}
            className={`pm-chip${picked ? " " + on : ""}`}
            aria-pressed={picked}
            onClick={() => toggle(key, c)}
          >
            {c}
          </button>
        );
      })}
    </div>
  );
  const rating = (key, label) => (
    <div className="pm-tri">
      <div className="pm-tri-l">{label}</div>
      <Seg3 label={label} options={RATINGS} value={fb[key] || ""} onPick={(v) => save({ [key]: v })} />
    </div>
  );

  return (
    <div className="liab-card" data-testid="pm-feedback">
      <div className="liab-block">
        {rating("crew", "The crew")}
        {rating("care", "Care of belongings")}
        {rating("sales", "Sales & communication")}
      </div>
      <div className="liab-block">
        <div>What stood out?</div>
        {chips("liked", LIKED, "on-good")}
        <div style={{ marginTop: 10 }}>What could we do better?</div>
        {chips("improve", IMPROVE, "on-bad")}
      </div>
      <div className="liab-block">
        <div className="liab-top">
          <div>Would you recommend us?</div>
          <Seg3
            label="Would you recommend us?"
            options={RECOMMEND}
            value={fb.recommend || ""}
            onPick={(v) => save({ recommend: v })}
          />
        </div>
        {fb.recommend === "yes" ? (
          <div className="liab-sub" style={{ marginTop: 6, color: "var(--success)" }}>
            Great — ask for a referral or a public review.
          </div>
        ) : null}
      </div>
      <div className="liab-block">
        <div>The feedback from the client</div>
        <NoteTextarea
          job={job}
          fieldKey="pmClientQuote"
          onChange={set}
          rows={2}
          hint="e.g. The crew was on time and treated everything like their own..."
        />
      </div>
      <div className="liab-block">
        <div>Internal notes</div>
        <div className="field-row" style={{ margin: "7px 0 0 0" }}>
          <div>
            <div className="liab-sub" style={{ margin: "0 0 3px 0" }}>
              For the crew
            </div>
            <NoteTextarea job={job} fieldKey="feedbackOperations" onChange={set} rows={2} />
          </div>
          <div>
            <div className="liab-sub" style={{ margin: "0 0 3px 0" }}>
              For sales
            </div>
            <NoteTextarea job={job} fieldKey="crewFeedbackSalesperson" onChange={set} rows={2} />
          </div>
        </div>
        <div className="liab-sub" style={{ margin: "8px 0 3px 0" }}>
          Other notes
        </div>
        <NoteTextarea job={job} fieldKey="postMoveNotes" onChange={set} rows={2} hint="Anything else..." />
      </div>
    </div>
  );
};

/**
 * "Post-Escalation Notes" as a list, per the prototype (7.1): how each escalation was resolved
 * ("Resolved") and the MC's saved notes ("Note").
 */
const PostEscalationList = ({ job, jobEsc }) => {
  const rows = [];
  jobEsc
    .filter((e) => e.status === "resolved" && e.resolutionNote)
    .forEach((e) => rows.push({ text: e.resolutionNote, date: e.resolvedAt, tag: "Resolved" }));
  if (job.postEscalationNotes && !rows.some((r) => r.text === job.postEscalationNotes)) {
    rows.push({ text: job.postEscalationNotes, date: "", tag: "Note" });
  }
  const unique = rows.filter((r, i) => rows.findIndex((x) => x.text === r.text) === i);
  return (
    <>
      <SectionLabel icon="note">Post-Escalation Notes</SectionLabel>
      <div className="liab-card" data-testid="pm-esc-notes">
        {unique.length ? (
          unique.map((r, i) => (
            <div className="liab-block" key={i}>
              <div className="liab-top">
                <div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{r.text}</div>
                  <div className="liab-sub">MC{r.date ? " · " + fmtDateTime(r.date) : ""}</div>
                </div>
                <span className={`badge ${r.tag === "Resolved" ? "resolved" : "plain"}`}>{r.tag}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="liab-block">
            <div className="liab-sub">No post-escalation notes yet.</div>
          </div>
        )}
      </div>
    </>
  );
};

/** Post-move call attempts, LD and Local (Asana 1220092199815254). */
export const POST_MOVE_ATTEMPTS = 4;
const ATTEMPT_LABELS = ["1st attempt", "Second attempt", "Third attempt", "Fourth attempt"];

/**
 * One row per post-move call attempt: "Done" with when it was logged, or
 * "Mark done". Shares the list with the footer's attempt button. Read-only
 * once the post-move call was made or the job was closed.
 */
const PostMoveAttempts = ({ job, actions }) => {
  const attempts = Array.isArray(job.postMoveAttempts) ? job.postMoveAttempts : [];
  const inPostMove = String(job.status || "").startsWith("post-move");
  if (!inPostMove && !attempts.length) return null;
  const canLog = inPostMove && !job.postMoveCallDone && !job.postMoveClosed;

  return (
    <>
      <SectionLabel icon="phone">Post-Move Call Attempts</SectionLabel>
      <div className="liab-card" data-testid="pm-attempts">
        {ATTEMPT_LABELS.map((label, i) => {
          const n = i + 1;
          const done = attempts.find((a) => a.n === n);
          return (
            <div className="liab-block" key={n} data-testid="pm-attempt">
              <div className="liab-top">
                <div>
                  <div>{label}</div>
                  <div className="liab-sub">
                    {done ? "Done " + fmtDateTime(done.date) : canLog ? "Not made yet" : "Not made"}
                  </div>
                </div>
                {done ? (
                  <span className="badge resolved">
                    <Icon name="check" />
                    Done
                  </span>
                ) : canLog ? (
                  <button
                    type="button"
                    className="btn small"
                    aria-label={`Mark the ${label.toLowerCase()} done`}
                    onClick={() => actions.logPostMoveAttemptN(job.id, n)}
                  >
                    <Icon name="check" />
                    Mark done
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export const TabPostMove = ({ job, isLD, escalations, actions }) => {
  const set = makeS2Setter(job, actions.setS2Note);
  const isVIP = job.status === "post-move-vip";
  const jobEsc = escalations.filter((e) => e.jobId === job.id);
  const openCount = jobEsc.filter((e) => e.status === "open").length;

  return (
    <>
      {/* Hidden until the footer's "Schedule callback" asks for it (post-move call attempts). */}
      <CallbackWidget job={job} onSave={(d, t) => actions.saveCallback(job.id, d, t)} />

      {isLD ? (
        <>
          <SectionLabel icon="truck">Actual Delivery Date</SectionLabel>
          <div className="field">
            <input
              type="date"
              aria-label="Actual delivery date"
              value={job.actualDeliveryDate || job.deliveryWindowEnd || ""}
              onChange={(e) => actions.setJobField(job.id, "actualDeliveryDate", e.target.value)}
            />
            <div className="liab-sub" style={{ marginTop: 3 }}>
              From the Delivery Day screen — editable
            </div>
          </div>
        </>
      ) : null}

      <AiCallBlock job={job} type="post_move" />

      <SectionLabel icon="activity">Salesperson</SectionLabel>
      <SalespersonField job={job} />

      <SectionLabel icon="activity">Assigned Crew</SectionLabel>
      <PlannerCrew job={job} />

      <SectionLabel icon="check">Pricing</SectionLabel>
      <ReadField label="Total price of the move">${job.price}</ReadField>
      {/* Hourly jobs: first contract vs the updated contract after the service (Asana 1219446236932314). */}
      <HourlyFinal job={job} />
      <S2Field job={job} fieldKey="priceAdjustments" label="Price adjustments" onChange={set} />

      <SectionLabel icon="alert">Escalations During the Move</SectionLabel>
      <div
        className="dest-card"
        style={{
          background: openCount
            ? "linear-gradient(135deg, var(--danger-light) 0%, #fff 60%)"
            : "linear-gradient(135deg, var(--success-light) 0%, #fff 60%)",
          borderColor: openCount ? "#f3c6c6" : "#bfe6c9",
          marginBottom: 14,
        }}
      >
        <div className="dest-card-head" style={{ marginBottom: 0 }}>
          <div
            className="dest-card-icon"
            style={{
              background: openCount ? "var(--danger)" : "var(--success)",
              boxShadow: "none",
            }}
          >
            <Icon name="alert" />
          </div>
          <div>
            <div
              className="dest-card-label"
              style={{ color: openCount ? "var(--danger)" : "var(--success)" }}
            >
              {openCount
                ? `${openCount} Open Escalation${openCount > 1 ? "s" : ""}`
                : "All Clear"}
            </div>
            <div className="dest-card-address">
              {openCount
                ? "Needs Ops/Sales attention"
                : "No open escalations on this move"}
            </div>
          </div>
        </div>
      </div>
      {jobEsc.map((e) => (
        <div className="note-item" key={e.id}>
          <span className={`badge ${e.status}`}>
            <span className="bdot" />
            {e.type}
          </span>{" "}
          {e.note}
          <div className="meta">{fmtDateTime(e.createdAt)}</div>
        </div>
      ))}

      <PostEscalationList job={job} jobEsc={jobEsc} />

      <SectionLabel icon="note">Feedback during the Move</SectionLabel>
      <div className="field">{job.inProgressFeedback || "—"}</div>

      {isVIP ? (
        <>
          <SectionLabel icon="note">Automated Actions</SectionLabel>
          <div className="toolbar-row" style={{ marginTop: 0 }}>
            <span className="badge plain">
              <span className="bdot" style={{ background: "var(--success)" }} />
              Rate your move
            </span>
            <span className="badge plain">
              <span className="bdot" style={{ background: "var(--success)" }} />
              Missing items / claims
            </span>
            <span className="badge plain">
              <span className="bdot" style={{ background: "var(--success)" }} />
              Referral request (if happy)
            </span>
          </div>
        </>
      ) : null}

      <SectionLabel icon="note">Feedback &amp; Review</SectionLabel>
      {isLD ? (
        <PmFeedbackCard job={job} actions={actions} set={set} />
      ) : (
        <>
      <div className="field">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <label style={{ marginBottom: 0 }}>Feedback for the Crew</label>
        </div>
        <NoteTextarea job={job} fieldKey="feedbackOperations" onChange={set} rows={3} />
      </div>
      <div className="field">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <label style={{ marginBottom: 0 }}>Crew Feedback for the Salesperson</label>
        </div>
        <NoteTextarea job={job} fieldKey="crewFeedbackSalesperson" onChange={set} rows={3} />
      </div>
      <div className="field">
        <label>Notes</label>
        <NoteTextarea job={job} fieldKey="postMoveNotes" onChange={set} rows={4} />
      </div>
      <div className="toolbar-row" style={{ marginBottom: 14 }}>
        <SaveButton
          primary
          idleIcon="note"
          label="Request a Review"
          savedLabel="Review requested"
          onSave={() => actions.requestReview(job.id, "Feedback")}
        />
      </div>
        </>
      )}

      <SectionLabel icon="activity">Automated Move Flow</SectionLabel>
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-body" style={{ maxHeight: "none", padding: 14 }}>
          {/* Nothing is sent from here yet: the AI calls are placed outside the
              dashboard (Asana 1219524311157587), so this only marks the job. */}
          {job.automatedFlowStarted ? (
            <span className="badge plain" style={{ marginBottom: 10 }}>
              <span className="bdot" />
              Marked for automated check-in · not sent yet
            </span>
          ) : (
            <button
              className="btn primary small"
              onClick={() => actions.startAutomatedFlow(job.id)}
            >
              <Icon name="activity" />
              Mark for automated check-in
            </button>
          )}
          <S2Field
            job={job}
            fieldKey="automatedFlowNotes"
            label="Notes for the automated flow"
            onChange={set}
          />
        </div>
      </div>

      <PmIssues job={job} actions={actions} />

      <PostMoveAttempts job={job} actions={actions} />

      {job.postMoveClosed ? null : (
        <div className="toolbar-row" style={{ marginTop: 14, alignItems: "center" }}>
          <PostMoveCallDone job={job} actions={actions} />
          {job.postMoveCallDone ? null : <span className="pm-hint">Call the client first to complete the Post Move</span>}
        </div>
      )}

      {job.postMoveClosed ? (
        <div className="toolbar-row" style={{ marginTop: 14 }}>
          <span className="badge resolved">
            <span className="bdot" />
            Closed
          </span>
        </div>
      ) : null}
    </>
  );
};
